import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { createContext, useContext, useReducer, useState } from 'react';
import { PersonaPresence } from 'office-ui-fabric-react';
import getProfilePicWithOptimize from '../helpers/ProfilePicOptimizeHelper';
import { useAuthStore } from '@stores/authStore';
import axios from 'axios';
import { cancelInFlightPresenceRequests, cancelInFlightProfilePhotoRequests, fetchPresence } from '../helpers/GraphHelpers';
var idMap = new Map();
export var PersonaContext = /*#__PURE__*/ createContext({
    getImg: function getImg(upn, options) {},
    getPresence: function getPresence(upn, oid) {},
    resetState: function resetState() {},
    getUser: function getUser(upn, props) {},
    getDrs: function getDrs(upn, oid) {},
    state: {
        images: {},
        presences: {},
        users: {},
        drs: {}
    },
    handleSetIsPrinting: function handleSetIsPrinting(b) {},
    isPrinting: false
});
var reducer = function reducer(state, action) {
    switch(action.type){
        case 'loading-presence':
            {
                var newState = _object_spread({}, state);
                newState.presences[action.upn] = _object_spread_props(_object_spread({}, newState.presences[action.upn] || {}), {
                    presence: PersonaPresence.none,
                    isLoading: true
                });
                return newState;
            }
        case 'set-presence':
            {
                var newState1 = _object_spread({}, state);
                newState1.presences[action.upn] = _object_spread_props(_object_spread({}, newState1.presences[action.upn] || {}), {
                    presence: action.presence,
                    expirationTime: new Date().setMinutes(new Date().getMinutes() + 10),
                    isLoading: false
                });
                return newState1;
            }
        case 'loading-image':
            {
                var newState2 = _object_spread({}, state);
                newState2.images[action.upn] = _object_spread_props(_object_spread({}, newState2.images[action.upn] || {}), {
                    image: '',
                    isLoading: true
                });
                return newState2;
            }
        case 'set-image':
            {
                var newState3 = _object_spread({}, state);
                newState3.images[action.upn] = _object_spread_props(_object_spread({}, newState3.images[action.upn] || {}), {
                    image: action.image,
                    isLoading: false
                });
                return newState3;
            }
        case 'loading-user':
            {
                var newState4 = _object_spread({}, state);
                newState4.users[action.upn] = _object_spread_props(_object_spread({}, newState4.users[action.upn] || {}), {
                    user: null,
                    isLoading: true
                });
                return newState4;
            }
        case 'set-user':
            {
                var newState5 = _object_spread({}, state);
                newState5.users[action.upn] = _object_spread_props(_object_spread({}, newState5.users[action.upn] || {}), {
                    user: action.user,
                    isLoading: false
                });
                return newState5;
            }
        case 'loading-drs':
            {
                var newState6 = _object_spread({}, state);
                newState6.drs[action.upn] = _object_spread_props(_object_spread({}, newState6.drs[action.upn] || {}), {
                    drs: null,
                    isLoading: true
                });
                return newState6;
            }
        case 'set-drs':
            {
                var newState7 = _object_spread({}, state);
                newState7.drs[action.upn] = _object_spread_props(_object_spread({}, newState7.drs[action.upn] || {}), {
                    drs: action.drs,
                    isLoading: false
                });
                return newState7;
            }
        case 'reset':
            {
                return action.initialState;
            }
    }
};
var staticRequestLoadingState = {
    presences: {},
    images: {},
    users: {},
    drs: {}
};
/**
 * Cancels in-flight avatar (/api/user/photo) and Graph presence requests and clears the Persona
 * in-flight guards so a pagination/filter change can free HTTP/1.1 sockets for the table request.
 * Cached blob URLs are kept — only pending fetches are aborted.
 */ export var cancelInFlightPersonaMediaRequests = function cancelInFlightPersonaMediaRequests() {
    cancelInFlightProfilePhotoRequests();
    cancelInFlightPresenceRequests();
    Object.keys(staticRequestLoadingState.images).forEach(function(upn) {
        staticRequestLoadingState.images[upn] = false;
    });
    Object.keys(staticRequestLoadingState.presences).forEach(function(upn) {
        staticRequestLoadingState.presences[upn] = false;
    });
};
var PersonaContextProvider = function PersonaContextProvider(props) {
    var _useState = _sliced_to_array(useState(false), 2), isPrinting = _useState[0], setIsPrinting = _useState[1];
    var staticRequestErrorState = React.useRef({
        presences: {},
        images: {},
        users: {},
        drs: {}
    });
    var _useReducer = _sliced_to_array(useReducer(reducer, {
        presences: {},
        images: {},
        users: {},
        drs: {}
    }, function(x) {
        return x;
    }), 2), state = _useReducer[0], dispatch = _useReducer[1];
    var resetState = function resetState() {
        dispatch({
            type: 'reset',
            initialState: {
                presences: {},
                images: {},
                users: {},
                drs: {}
            }
        });
    };
    React.useEffect(function() {
        var div = window.document.createElement('div');
        window.document.body.appendChild(div);
        div.setAttribute('id', 'mousePosition');
        var listener = function listener(e) {
            if (!(div === null || div === void 0 ? void 0 : div.dataset) || !(e === null || e === void 0 ? void 0 : e.clientX) || !(e === null || e === void 0 ? void 0 : e.clientY)) return;
            div.dataset.x = e.clientX.toString();
            div.dataset.y = e.clientY.toString();
        };
        window.onmousemove = listener;
        return function() {
            window.document.body.removeChild(div);
            window.onmousemove = null;
        };
    }, []);
    var getImg = function getImg(upn, options) {
        return _async_to_generator(function() {
            var _options_signal, image, _options_signal1, img, img1;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        image = (state.images[upn] || {}).image;
                        if (image || staticRequestErrorState.current.images[upn] > 0) {
                            return [
                                2
                            ];
                        }
                        if (!staticRequestLoadingState.images[upn]) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            getProfilePicWithOptimize({
                                upn: upn,
                                signal: options === null || options === void 0 ? void 0 : options.signal
                            })
                        ];
                    case 1:
                        img = _state.sent();
                        if (!(options === null || options === void 0 ? void 0 : (_options_signal1 = options.signal) === null || _options_signal1 === void 0 ? void 0 : _options_signal1.aborted) && img) {
                            dispatch({
                                type: 'set-image',
                                upn: upn,
                                image: img
                            });
                        }
                        return [
                            2
                        ];
                    case 2:
                        staticRequestLoadingState.images[upn] = true;
                        dispatch({
                            type: 'loading-image',
                            upn: upn
                        });
                        return [
                            4,
                            getProfilePicWithOptimize({
                                upn: upn,
                                signal: options === null || options === void 0 ? void 0 : options.signal,
                                handleError: function handleError(e) {
                                    if (e.statusCode !== 429) {
                                        if (staticRequestErrorState.current.images[upn]) {
                                            staticRequestErrorState.current.images[upn]++;
                                        } else {
                                            staticRequestErrorState.current.images[upn] = 1;
                                        }
                                    }
                                }
                            })
                        ];
                    case 3:
                        img1 = _state.sent();
                        staticRequestLoadingState.images[upn] = false;
                        if (options === null || options === void 0 ? void 0 : (_options_signal = options.signal) === null || _options_signal === void 0 ? void 0 : _options_signal.aborted) {
                            return [
                                2
                            ];
                        }
                        dispatch({
                            type: 'set-image',
                            upn: upn,
                            image: img1 || ''
                        });
                        return [
                            2
                        ];
                }
            });
        })();
    };
    var getUser = function getUser(upn, props) {
        return _async_to_generator(function() {
            var user, type, u, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        user = (state.users[upn] || {}).user;
                        if (user || staticRequestLoadingState.users[upn] || staticRequestErrorState.current.users[upn] > 0) {
                            return [
                                2
                            ];
                        }
                        dispatch({
                            type: 'loading-user',
                            upn: upn
                        });
                        staticRequestLoadingState.users[upn] = true;
                        type = props ? props.type : undefined;
                        return [
                            4,
                            axios.get('/api/common/getUserForPersona', {
                                params: {
                                    upn: upn,
                                    type: type
                                }
                            })
                        ];
                    case 1:
                        u = _state.sent();
                        idMap.set(upn, u.data.id);
                        dispatch({
                            type: 'set-user',
                            upn: upn,
                            user: u.data
                        });
                        return [
                            3,
                            3
                        ];
                    case 2:
                        e = _state.sent();
                        console.log(e);
                        if (staticRequestErrorState.current.users[upn]) {
                            staticRequestErrorState.current.users[upn]++;
                        } else {
                            staticRequestErrorState.current.users[upn] = 1;
                        }
                        return [
                            3,
                            3
                        ];
                    case 3:
                        staticRequestLoadingState.users[upn] = false;
                        return [
                            2
                        ];
                }
            });
        })();
    };
    var getDrs = function getDrs(upn, oid) {
        return _async_to_generator(function() {
            var drs, d, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        drs = (state.drs[upn] || {}).drs;
                        if (drs || staticRequestLoadingState.drs[upn]) {
                            return [
                                2
                            ];
                        }
                        staticRequestLoadingState.drs[upn] = true;
                        dispatch({
                            type: 'loading-drs',
                            upn: upn
                        });
                        return [
                            4,
                            axios.get('/api/common/get-drs', {
                                params: {
                                    upn: upn,
                                    oid: oid || idMap.get(upn)
                                }
                            })
                        ];
                    case 1:
                        d = _state.sent();
                        dispatch({
                            type: 'set-drs',
                            upn: upn,
                            drs: d.data
                        });
                        return [
                            3,
                            3
                        ];
                    case 2:
                        e = _state.sent();
                        console.log(e);
                        dispatch({
                            type: 'set-drs',
                            upn: upn,
                            drs: 'NO_DRS'
                        });
                        return [
                            3,
                            3
                        ];
                    case 3:
                        staticRequestLoadingState.drs[upn] = false;
                        return [
                            2
                        ];
                }
            });
        })();
    };
    var getPresence = function getPresence(upn, oid) {
        return _async_to_generator(function() {
            var _state_users_upn_user, _state_users_upn, _ref, presence, expirationTime, token, id, p, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            3,
                            ,
                            4
                        ]);
                        if (!upn) {
                            return [
                                2
                            ];
                        }
                        _ref = state.presences[upn] || {}, presence = _ref.presence, expirationTime = _ref.expirationTime;
                        if (staticRequestLoadingState.presences[upn]) {
                            return [
                                2
                            ];
                        }
                        if (presence != null && expirationTime > new Date().getTime()) {
                            return [
                                2
                            ];
                        }
                        return [
                            4,
                            useAuthStore.getState().getValidGraphToken()
                        ];
                    case 1:
                        token = _state.sent();
                        if (!token) {
                            return [
                                2
                            ];
                        }
                        staticRequestLoadingState.presences[upn] = true;
                        dispatch({
                            type: 'loading-presence',
                            upn: upn
                        });
                        id = oid || (state === null || state === void 0 ? void 0 : (_state_users_upn = state.users[upn]) === null || _state_users_upn === void 0 ? void 0 : (_state_users_upn_user = _state_users_upn.user) === null || _state_users_upn_user === void 0 ? void 0 : _state_users_upn_user.id) || idMap.get(upn);
                        if (!id) {
                            staticRequestLoadingState.presences[upn] = false;
                            return [
                                2
                            ];
                        }
                        return [
                            4,
                            fetchPresence(id, token)
                        ];
                    case 2:
                        p = _state.sent();
                        dispatch({
                            type: 'set-presence',
                            upn: upn,
                            presence: p
                        });
                        return [
                            3,
                            4
                        ];
                    case 3:
                        e = _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 4:
                        staticRequestLoadingState.presences[upn] = false;
                        return [
                            2
                        ];
                }
            });
        })();
    };
    return /*#__PURE__*/ _jsx(PersonaContext.Provider, {
        value: {
            getImg: getImg,
            getPresence: getPresence,
            getUser: getUser,
            getDrs: getDrs,
            state: state,
            isPrinting: isPrinting,
            handleSetIsPrinting: function handleSetIsPrinting(boolean) {
                return setIsPrinting(boolean);
            },
            resetState: resetState
        },
        children: props.children
    });
};
var usePersona = function usePersona() {
    return useContext(PersonaContext);
};
export { PersonaContextProvider, usePersona };
